import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { Trash2, AlertCircle, Loader2 } from "lucide-react";
import ProjectInfoService from "../services/ProjectInfoService";
import { ProjectInfoFields } from "../services/ProjectInfoService";
import apiClient from "../utils/apiClient";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import ErrorBanner from "../common/ErrorBanner";
import FileUploadZone from "../uploads/FileUploadZone";
import FileTable from "../uploads/FileTable";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";

const EmptyState = ({ message }) => (
  <div className="p-4 text-center text-gray-500">
    {message || "No data available."}
  </div>
);

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
  </div>
);

const ErrorScreen = ({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
    <p className="text-red-600">Error: {message}</p>
  </div>
);

const SensorPlacement = () => {
  const { projectId } = useParams();

  const [scenarios, setScenarios] = useState([]);
  const [newScenario, setNewScenario] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  const token = localStorage.getItem("token");

  const apiRoot = process.env.REACT_APP_API_ROOT;
  const apiRootOperational = process.env.REACT_APP_API_ROOT_OPERATIONAL;

  const projectInfoService = new ProjectInfoService(apiRoot, token);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  useEffect(() => {
    fetchScenarios();
  }, [projectId]);

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiClient.get("/api/project_info/", {
        params: { project_id: projectId },
        showToast: false,
      });

      let scenarioList = data[0]?.sensor_placement_zone_config;
      if (typeof scenarioList === "string") {
        scenarioList = JSON.parse(scenarioList);
      }

      setScenarios(Array.isArray(scenarioList) ? scenarioList : []);
    } catch (err) {
      console.error("Error fetching scenarios:", err);
      setError("Failed to load scenarios. Please try again.");
      showToast("Failed to fetch scenarios", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelect = (files) => {
    if (files.length === 0) return;

    const file = files[0];
    setSelectedFile(file);
  };

  const handleDeleteScenarios = async () => {
    if (selectedScenarios.length === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedScenarios.length} scenario(s)?`
    );
    if (!confirmDelete) return;

    try {
      setIsProcessing(true);

      for (const scenario of selectedScenarios) {
        try {
          await apiClient.delete(
            `/api/sensor_placement?project_id=${encodeURIComponent(
              projectId
            )}&scenario=${encodeURIComponent(scenario)}`,
            {
              useOperational: true,
              showToast: false,
            }
          );
        } catch (deleteError) {
          if (
            deleteError.message &&
            deleteError.message.includes("no records found")
          ) {
            console.log(
              `No backend records found for scenario ${scenario} - continuing with UI update`
            );
          } else {
            console.warn(
              `Error deleting backend records for scenario ${scenario}:`,
              deleteError
            );
          }
        }
      }

      const currentInfo = await projectInfoService.getProjectInfo(projectId);
      let sensorPlacementZones = projectInfoService.parseJsonField(
        currentInfo.sensor_placement_zone_config
      );
      sensorPlacementZones = sensorPlacementZones.filter(
        (s) => !selectedScenarios.includes(s)
      );

      await projectInfoService.updateProjectInfo(projectId, {
        [ProjectInfoFields.SENSOR_PLACEMENT_ZONE_CONFIG]:
          projectInfoService.stringifyJsonField(sensorPlacementZones),
      });

      setScenarios(sensorPlacementZones);
      setSelectedScenarios([]);
      showToast(`Selected scenarios deleted successfully.`);
    } catch (err) {
      console.error("Error updating project info:", err);
      showToast(err.message || "Failed to update scenarios", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectScenario = (scenarioId) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      } else {
        return [...prev, scenarioId];
      }
    });
  };

  const handleSelectAllScenarios = (newSelectedScenarios) => {
    setSelectedScenarios(newSelectedScenarios);
  };

  const handleUploadScenario = async () => {
    if (!newScenario || !selectedFile) {
      showToast("Please enter a scenario name and select a file.", "error");
      return;
    }

    try {
      setIsProcessing(true);

      const data = await apiClient.uploadFile(
        "/api/sensor_placement",
        selectedFile,
        {
          project_id: projectId,
          zone: newScenario,
        },
        {
          useOperational: true,
          showToast: false,
        }
      );

      await projectInfoService.updateListField(
        projectId,
        "sensor_placement_zone_config",
        newScenario,
        { overwrite: false, unique: true }
      );

      setScenarios((prev) => [...new Set([...prev, newScenario])]);

      setNewScenario("");
      setSelectedFile(null);

      showToast(
        `Scenario "${newScenario}" uploaded successfully. ${
          data.records ? `${data.records} records processed.` : ""
        }`
      );
    } catch (err) {
      console.error("Error in scenario upload process:", err);
      showToast(err.message || "Error uploading scenario", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingScreen />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <ErrorScreen message={error} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Sensor Placement - Project ${projectId}`}
            subtitle="Manage and analyze different sensor scenarios"
          >
            <div className="space-y-6 p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader
                    title="Manage Scenarios"
                    action={
                      selectedScenarios.length > 0 && (
                        <PrimaryButton
                          onClick={handleDeleteScenarios}
                          isLoading={isProcessing}
                          loadingText="Deleting..."
                          className="bg-red-600 hover:bg-red-700"
                          width="auto"
                        >
                          Delete Selected ({selectedScenarios.length})
                        </PrimaryButton>
                      )
                    }
                  />

                  <div>
                    {scenarios.length > 0 ? (
                      <FileTable
                        files={scenarios.map((scenario) => ({
                          id: scenario,
                          name: scenario,
                          type: "Scenario",
                          _created_at: null,
                          _updated_at: null,
                        }))}
                        selectedFiles={selectedScenarios}
                        onSelectFile={handleSelectScenario}
                        onSelectAll={handleSelectAllScenarios}
                      />
                    ) : (
                      <EmptyState message="No scenarios available. Upload a new scenario to get started." />
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Upload New Scenario" />

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="scenario-name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Scenario Name
                      </label>
                      <input
                        id="scenario-name"
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter scenario name"
                        value={newScenario}
                        onChange={(e) => setNewScenario(e.target.value)}
                      />
                    </div>

                    <FileUploadZone
                      onFilesSelect={handleFilesSelect}
                      disabled={isProcessing}
                      accept=".xlsx,.xls"
                      acceptedTypes={["XLSX", "XLS"]}
                    />

                    <PrimaryButton
                      onClick={handleUploadScenario}
                      isLoading={isProcessing}
                      loadingText="Processing..."
                      disabled={!selectedFile || !newScenario}
                    >
                      Upload Scenario
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default SensorPlacement;
